'use client';

/**
 * ui-future always-on chrome — VERBATIM port of Control Room v4 stage:
 * - fixed 1920×1080 stage scaled to fit (fitStyle: min(w/1920,h/1080), 200ms poll)
 * - stage background radial-gradients + star dots (spec line 34)
 * - gaze-parallax floor (dashed ellipses + conic sweep, translate (960-gx)*.014/(430-gy)*.02)
 * - metal logotype row (Michroma 14 ls .34em) + LIVE row (dot + bccRing ping)
 * - BEAST core orb: 96px sphere (radial-gradient #eafcff→#7de8ff→#0f4a5c) +
 *   two 3D-tilted spinning rings (rotateX 72° / rotateZ 58° rotateX 74°,
 *   bccSpin 6s / 11s reverse) + label; room transform translate(846,-58) scale(.42)
 * - light beam under orb (hangar), bottom legends, voice strip
 * - gaze reticle 56px: ring + center dot + 4 ticks + lock corner brackets +
 *   SCANNING / LOCK · X label. The reticle IS the cursor (zero easing).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { registerVoiceCommand } from '@/future/voice/commands';
const FerrofluidOrb = dynamic(() => import('@/future/chrome/FerrofluidOrb'), { ssr: false });

const MONO = 'var(--uif-font-mono)';

/* ── fit law: 1920×1080 stage scaled to viewport ────────────────── */
function useFit() {
  const [fit, setFit] = useState(1);
  useEffect(() => {
    const calc = () => Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
    const apply = () => { const f = calc(); setFit(p => Math.abs(p - f) > 0.001 ? f : p); };
    apply();
    window.addEventListener('resize', apply);
    const poll = setInterval(apply, 200);
    return () => { window.removeEventListener('resize', apply); clearInterval(poll); };
  }, []);
  return fit;
}

/* ── gaze: stage-space pointer + lock label (data-lock / interactive) ── */
function useGaze(fit: number, stageRef: React.RefObject<HTMLDivElement | null>) {
  const [gaze, setGaze] = useState({ gx: 960, gy: 430 });
  const [locked, setLocked] = useState<string | null>(null);
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if ((window as any).__uifDrag) return; // orb/panel drag in progress — background must not follow (Boss 2026-07-10)
      const el = stageRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setGaze({ gx: (e.clientX - r.left) / fit, gy: (e.clientY - r.top) / fit });
      const t = (e.target as HTMLElement).closest?.('[data-lock],button,a,[role="button"]') as HTMLElement | null;
      setLocked(t ? (t.dataset.lock || t.getAttribute('aria-label') || 'OPEN') : null);
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, [fit, stageRef]);
  return { ...gaze, locked };
}

function LiveRow() {
  return (
    <div style={{ position: 'absolute', right: 36, top: 26, display: 'flex', alignItems: 'center', gap: 12, fontFamily: MONO, fontSize: 18, letterSpacing: '.18em', color: '#56788c', zIndex: 40 }}>
      <span style={{ position: 'relative', width: 7, height: 7 }}>
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#5df2b4' }} />
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid #5df2b4', animation: 'bccRing 2.8s ease-out infinite' }} />
      </span>
      <span style={{ color: '#9fb8c6' }}>LIVE · AUTO-REFRESH 60S</span>
    </div>
  );
}

/* ── BEAST push sphere — v6 §5: draggable orb + ❯ page chat, wired to the
   REAL Beast main session (POST /api/gw/chat — NO Gemini, Boss 2026-07-10). Homes: hangar (880,130) full size;
   rooms dock top-right (translate 846,-58 scale .42). Drag → float at .72,
   position persists (bcc.orb.pos). ❯ arrow → 560×≤640 page-chat panel;
   one thread per page context, persisted (bcc.ia.threads, last 24 msgs). */
const RealChat = dynamic(() => import('@/components/chat/Chat').then(m => m.Chat), { ssr: false });
// Live orb → the clean single-brain listener (Boss 2026-07-18). Old 1046-line
// components/voice/BeastMode.tsx kept on disk as salvage source until confirmed good.
const RealBeastMode = dynamic(() => import('@/future/chrome/BeastMode').then(m => m.BeastMode), { ssr: false });
const RealSessionChat = dynamic(() => import('@/components/chat/SessionChat').then(m => m.SessionChat), { ssr: false });

/* A session torn OUT of the main chat panel into its own floating window (Boss 2026-07-13
 * 'I can grab the beast and pull it out into a separate chat next to it ... or put it back and it
 * embeds into the main one'). Draggable + resizable in stage-local units (delta / fit, matching
 * the main panel). Claims the voice target on any pointer/focus inside it, so whichever window the
 * cursor is in owns the spoken turn (focus-follows-cursor). Mounts the SAME real chat components
 * as the main panel — never a rewrite of the streaming path. slug=null → the Beast main chat. */
type DetachedWin = { id: string; slug: string | null; name: string; pos: { x: number; y: number }; size: { w: number; h: number }; z?: number; url?: string; anchorSlug?: string | null };
function DetachedChat({ win, active, fit, dockZone, onDock, onMove, onRaise }: {
  win: DetachedWin; active?: boolean; fit: number; dockZone: () => DOMRect | null;
  onDock: (id: string) => void; onMove: (id: string, pos: { x: number; y: number }, size?: { w: number; h: number }) => void;
  onRaise: (id: string) => void;
}) {
  const target = win.slug ?? '__beast';
  // Clicking (or focusing) a card claims the voice target AND raises it above the others
  // (Boss 2026-07-14 'click on card and it pops over the other cards').
  // A GAME window (win.url) is not a chat — it must NEVER claim the voice target, or tapping the
  // game would hijack Claire's mic to __beast (Boss 2026-07-15). Only real chat cards claim.
  const claim = () => { if (!win.url) (window as any).__beastVoiceTarget = target; onRaise(win.id); };
  /* CLAIM WITHOUT A CLICK (Boss 2026-07-15 'conversations go only to Beast, not inside Claire'):
     focus-follows-cursor only claimed on a pointer/focus INSIDE the card, so a card that was merely
     opened or raised never owned the mic and every turn fell back to the Beast main chat. The
     active (raised) card now owns the voice target on its own — and a lone card claims on mount —
     so spoken turns land in THAT session's chat without Boss having to click into it first. */
  useEffect(() => { if (!win.url) (window as any).__beastVoiceTarget = target; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (active && !win.url) (window as any).__beastVoiceTarget = target; }, [active, target]);
  const beginDrag = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('textarea,input,button,a,select,[contenteditable="true"],[data-nodrag],.msg-bubble')) return;
    claim();
    const sx = e.clientX, sy = e.clientY, bx = win.pos.x, by = win.pos.y;
    let started = false, overDock = false;
    const move = (ev: PointerEvent) => {
      if (!started && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 6) return;
      started = true; (window as any).__uifDrag = true;
      onMove(win.id, { x: bx + (ev.clientX - sx) / fit, y: by + (ev.clientY - sy) / fit });
      const dz = dockZone();
      overDock = !!dz && ev.clientX >= dz.left && ev.clientX <= dz.right && ev.clientY >= dz.top && ev.clientY <= dz.bottom;
      (window as any).__uifDockHot = overDock ? win.id : null;
    };
    const up = () => {
      (window as any).__uifDrag = false; (window as any).__uifDockHot = null;
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      if (overDock) onDock(win.id); // dropped onto the main panel → re-embed
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  return (
    <div onPointerDownCapture={claim} onFocusCapture={claim} onPointerDown={beginDrag}
      style={{
        position: 'absolute', left: 0, top: 0, transform: `translate(${win.pos.x}px,${win.pos.y}px)`,
        width: win.size.w, height: win.size.h, borderRadius: 14, pointerEvents: 'auto',
        border: win.slug === 'claire'
          ? (active ? '2px solid rgba(255,145,215,.98)' : '2px solid rgba(255,150,210,.7)')
          : (active ? '1.5px solid rgba(150,230,255,.95)' : '1px solid rgba(180,154,255,.5)'),
        background: win.slug === 'claire'
          ? 'linear-gradient(170deg,rgba(255,120,200,.20),rgba(90,150,255,.16) 60%,rgba(8,14,30,.96))'
          : 'linear-gradient(170deg,rgba(60,40,120,.16),rgba(4,10,18,.97) 55%)',
        boxShadow: active
          ? '0 40px 100px -24px rgba(0,0,0,.9),0 0 0 1px rgba(150,230,255,.4),0 0 42px -4px rgba(120,220,255,.7)'
          : '0 40px 100px -24px rgba(0,0,0,.9),0 0 60px -16px rgba(150,120,255,.3)',
        transition: 'box-shadow .25s ease, border-color .25s ease',
        padding: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: win.z ?? 90, cursor: 'grab',
      }}>
      <div style={{ fontFamily: MONO, fontSize: 14, letterSpacing: '.2em', color: '#c9b8ff', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 2px' }}>
        <span>⬚ {(win.name || 'BEAST').toUpperCase().slice(0, 20)}</span>
        <span data-nodrag onMouseUp={e => { e.stopPropagation(); onDock(win.id); }} title="dock back into the main chat"
          style={{ cursor: 'pointer', color: '#c9b8ff', border: '1px solid rgba(180,154,255,.4)', borderRadius: 5, padding: '2px 8px' }}>↥ DOCK</span>
      </div>
      <div className="uif-cardchat" style={{ flex: 1, minHeight: 0, overflow: 'hidden', borderRadius: 10, zoom: win.slug === 'claire' ? 1.6 : undefined }}>
        {win.url
          ? <iframe src={win.url} title={win.name} style={{ width: '100%', height: '100%', border: 0, borderRadius: 10 }} allow="autoplay" />
          : win.slug ? <RealSessionChat key={win.slug} slug={win.slug} name={win.name} /> : <RealChat />}
      </div>
      <div data-nodrag onPointerDown={(e) => {
        e.stopPropagation();
        const sx = e.clientX, sy = e.clientY, bw = win.size.w, bh = win.size.h;
        const mv = (ev: PointerEvent) => onMove(win.id, win.pos, {
          w: Math.max(280, Math.min(1400, bw + (ev.clientX - sx) / fit)),
          h: Math.max(220, Math.min(1000, bh + (ev.clientY - sy) / fit)),
        });
        const up = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
      }} title="drag to resize"
        style={{ position: 'absolute', right: 2, bottom: 2, width: 20, height: 20, cursor: 'nwse-resize', zIndex: 92, display: 'grid', placeItems: 'center', color: 'rgba(201,184,255,.6)' }}>
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M13 5 L5 13 M13 9 L9 13" /></svg>
      </div>
    </div>
  );
}

function CoreOrb() {
  const [space, setSpace] = useState('hangar');
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // start pointer (sx,sy in VIEWPORT px) + base orb pos (bx,by in STAGE units) — drag deltas are
  // divided by `fit` so the orb tracks the cursor 1:1 on a scaled stage (Boss 2026-07-11 fix).
  const [drag, setDrag] = useState<{ sx: number; sy: number; bx: number; by: number } | null>(null);
  const fit = useFit();
  const [open, setOpen] = useState(false);
  /* panel drag — Boss 2026-07-10: chat panel must be draggable, independent of the orb */
  const [panelPos, setPanelPos] = useState<{ x: number; y: number } | null>(null);
  // RESIZE (Boss 2026-07-11 'resize the assistant window ... drag it by the corner'): null = the
  // default 880×980; a drag on the corner grip sets an explicit size. RESET clears it.
  const [panelSize, setPanelSize] = useState<{ w: number; h: number } | null>(null);
  /* open gateway sessions — Boss 2026-07-10: see + switch live sessions inside the assistant */
  const [liveSessions, setLiveSessions] = useState<{ slug: string; name: string; state: string }[]>([]);
  const [allSessions, setAllSessions] = useState<{ slug: string; name: string; state: string }[]>([]);
  const [selSession, setSelSession] = useState<string | null>(null); // null = main Beast chat
  const [railOpen, setRailOpen] = useState(false); // ❮ sessions drawer expanding from the panel's left (Boss 2026-07-10)
  /* TEAR-OFF WINDOWS (Boss 2026-07-13): sessions pulled out of the panel into their own floating
     chats, worked side by side; drop one back on the main panel to re-embed. */
  const [detached, setDetached] = useState<DetachedWin[]>([]);
  // Which window Boss is actively working in — gets a glowing border so he always
  // knows the focused card (Boss 2026-07-14 'highlight the border I'm working on').
  const [activeId, setActiveId] = useState<string | null>(null);
  const mainPanelRef = useRef<HTMLDivElement>(null);
  const isDetached = (slug: string | null) => detached.some(d => d.slug === slug);
  const popOut = (slug: string | null, name: string) => {
    if (isDetached(slug)) return;
    const n = detached.length;
    setDetached(d => [...d, { id: `${slug ?? '__beast'}-${Date.now()}`, slug, name,
      pos: { x: 40 + n * 36, y: 120 + n * 36 }, size: { w: 620, h: 720 } }]);
    if (selSession === slug) setSelSession(null); // free the main panel back to Beast
    (window as any).__beastVoiceTarget = slug ?? '__beast';
  };
  const dockBack = (id: string) => { setActiveId(a => a === id ? null : a); setDetached(d => d.filter(w => w.id !== id)); };
  /* OPEN A GAME WINDOW ON THE STAGE (Boss 2026-07-15 'I want it in a separate window next to the
     chat'): a card (e.g. Claire's dress-up button) dispatches beast:open-game with a url; we add
     a detached iframe window docked to the RIGHT of the card that asked, so Boss sees chat + game
     side by side and can keep editing. Re-opening the same url just raises the existing window. */
  useEffect(() => {
    const onOpenGame = (e: Event) => {
      const { url, name, anchorSlug } = (e as CustomEvent).detail || {};
      if (!url) return;
      setDetached(d => {
        const existing = d.find(w => w.url === url);
        if (existing) { setActiveId(existing.id); return d; }
        const anchor = d.find(w => w.slug === anchorSlug);
        const gw = 460, gh = 820;
        // Open the game to the LEFT of the anchoring chat card (Boss 2026-07-15), clamped on-stage.
        const pos = anchor
          ? { x: Math.max(20, anchor.pos.x - gw - 24), y: anchor.pos.y }
          : { x: 120 + d.length * 36, y: 100 + d.length * 36 };
        const id = `game-${Date.now()}`;
        setActiveId(id);
        // Carry the anchor's slug (e.g. 'claire') so voice routing can still target the
        // owning person while her game window is the active card (Boss 2026-07-15: her voice
        // died because the game window had slug:null -> target fell back to '__beast').
        return [...d, { id, slug: null, anchorSlug: anchorSlug ?? null, url, name: name || 'GAME', pos, size: { w: gw, h: gh }, z: 200 }];
      });
    };
    window.addEventListener('beast:open-game', onOpenGame);
    return () => window.removeEventListener('beast:open-game', onOpenGame);
  }, []);
  // Click-to-front: bump the clicked window's z above every other (Boss 2026-07-14).
  const raiseWin = (id: string) => { setActiveId(id); setDetached(d => {
    // Keep voice routing on the raised window's owner (Boss 2026-07-15): clicking Claire's
    // game (slug:null) must still target 'claire' via its anchorSlug, not fall back to Beast.
    const cur0 = d.find(w => w.id === id);
    if (cur0) (window as any).__beastVoiceTarget = cur0.slug ?? cur0.anchorSlug ?? '__beast';
    const maxZ = d.reduce((m, w) => Math.max(m, w.z ?? 90), 90);
    const cur = cur0;
    if (cur && (cur.z ?? 90) >= maxZ) return d; // already on top
    return d.map(w => w.id === id ? { ...w, z: maxZ + 1 } : w);
  }); };
  const moveWin = (id: string, pos: { x: number; y: number }, size?: { w: number; h: number }) =>
    setDetached(d => d.map(w => {
      if (w.id !== id) return w;
      // Clamp to the 1920×1080 stage so a window can NEVER drift off-screen and vanish
      // (Boss 2026-07-14 'I moved it somewhere and it disappeared'). Keep the header reachable.
      const cx = Math.max(-((size ?? w.size).w) + 160, Math.min(1920 - 120, pos.x));
      const cy = Math.max(0, Math.min(1080 - 60, pos.y));
      return { ...w, pos: { x: cx, y: cy }, size: size ?? w.size };
    }));
  // Only the OPEN, visible Beast panel is a dock target. When minimized it's still mounted
  // (opacity:0/visibility:hidden) but its rect used to swallow any window dragged over that
  // hidden area → windows vanished on overlap (Boss 2026-07-14). Gate on `open`.
  const dockZone = () => (open ? (mainPanelRef.current?.getBoundingClientRect() ?? null) : null);
  useEffect(() => {
    if (!open) return;
    let dead = false;
    const pull = () => fetch('/api/gw/sessions').then(r => r.json())
      .then(d => {
        if (dead) return;
        const all = Array.isArray(d?.sessions) ? d.sessions : Array.isArray(d) ? d : [];
        const rank = (x: any) => (x.state === 'working' ? 0 : x.state === 'hibernating' ? 2 : 1);
        setAllSessions([...all].sort((x: any, y: any) => rank(x) - rank(y)));
        // tab strip: OPEN sessions only; the ❮ drawer lists everything incl. hibernating
        setLiveSessions(all.filter((s: any) => s.state !== 'hibernating')
          .sort((a: any, b: any) => (a.state === 'working' ? -1 : 1) - (b.state === 'working' ? -1 : 1)));
      })
      .catch(() => { });
    pull();
    const iv = setInterval(pull, 10000);
    return () => { dead = true; clearInterval(iv); };
  }, [open]);
  /* drag-anywhere — Boss 2026-07-10: grab the panel anywhere; a plain click (no move)
     passes through (cursor into input). Drags starting on interactive elements are excluded. */
  const beginPanelDrag = (e: React.PointerEvent, panelEl: HTMLElement) => {
    if ((e.target as HTMLElement).closest('textarea,input,button,a,select,[contenteditable="true"],[data-nodrag],.msg-bubble')) return;
    const scale = panelEl.getBoundingClientRect().width / panelEl.offsetWidth || 1; // cumulative stage scale
    const sx = e.clientX, sy = e.clientY, bx = panelPos?.x || 0, by = panelPos?.y || 0;
    const r0 = panelEl.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    /* KEEP THE WHOLE PANEL IN FRAME (Boss 2026-07-11 'I moved the assistant out of the frame
       down'): the old clamp only guaranteed a 140px/56px sliver stayed on screen, so the input
       and body could be dragged out of reach. Clamp every edge to the viewport with an 8px margin
       so the panel can never leave the frame. Math.max guards the case of a panel bigger than the
       viewport (min would exceed max). */
    const M = 8;
    const minDx = -(r0.left - M), maxDx = Math.max(-(r0.left - M), vw - M - (r0.left + r0.width));
    const minDy = -(r0.top - M), maxDy = Math.max(-(r0.top - M), vh - M - (r0.top + r0.height));
    let started = false;
    const move = (ev: PointerEvent) => {
      const dx = Math.min(Math.max(ev.clientX - sx, minDx), maxDx);
      const dy = Math.min(Math.max(ev.clientY - sy, minDy), maxDy);
      if (!started && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 6) return; // click, not drag
      if (!started) { started = true; panelDrag.current = { ox: sx, oy: sy }; (window as any).__uifDrag = true; }
      setPanelPos({ x: bx + dx / scale, y: by + dy / scale });
    };
    const up = () => {
      panelDrag.current = null; (window as any).__uifDrag = false;
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      if (started) { // swallow the click that follows a drag
        const kill = (ce: Event) => { ce.stopPropagation(); ce.preventDefault(); };
        window.addEventListener('click', kill, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', kill, { capture: true } as any), 100);
      }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const panelDrag = useRef<{ ox: number; oy: number } | null>(null);
  const [threads, setThreads] = useState<Record<string, { who: string; t: string }[]>>({});
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [listening, setListening] = useState(false);
  /* Beast Mode phase — classic-UI parity (Boss 2026-07-10): the orb SHOWS its state.
     idle cyan · listening amber · thinking violet · speaking green. */
  const [phase, setPhase] = useState<'idle' | 'listening' | 'thinking' | 'speaking'>('idle');
  /* PHASE HEARTBEAT (Boss 2026-07-13 'the orb is stuck blinking in thinking mode'): BeastMode
     re-emits the same phase repeatedly during a live turn (each tool step fires onPhase('thinking')).
     A plain [phase] effect wouldn't re-run on a same-value emission, so we bump this beat on EVERY
     emission — the outer watchdog below keys on it and re-arms only while the turn is actually alive. */
  const [phaseBeat, setPhaseBeat] = useState(0);
  const emitPhase = (p: 'idle' | 'listening' | 'thinking' | 'speaking') => { setPhase(p); setPhaseBeat(b => b + 1); };
  /* ORB CLOG WATCHDOG (Boss 2026-07-13): the innermost 20s watchdog lives inside BeastMode and only
     runs while that component is mounted, checking its OWN phase. The orb ring lives here in Chrome —
     if BeastMode unmounts, loses its stream, or never clears a tool phase, this ring blinks violet
     forever with nothing to reset it. This outer net force-returns the orb to idle if 'thinking'
     persists 30s with no fresh emission; a live turn keeps bumping phaseBeat and never trips it. */
  useEffect(() => {
    if (phase !== 'thinking') return;
    const t = setTimeout(() => { console.warn('[orb] outer clog watchdog — thinking stuck 30s with no update; forcing idle'); setPhase('idle'); }, 30_000);
    return () => clearTimeout(t);
  }, [phase, phaseBeat]);
  const moved = useRef(false);
  const recRef = useRef<{ alive: boolean; stream: MediaStream; ctx: AudioContext; rec: MediaRecorder | null } | null>(null); // Beast Mode conversation handle

  useEffect(() => {
    const on = (e: Event) => setSpace(String((e as CustomEvent).detail || 'hangar'));
    window.addEventListener('uif:space', on);
    // Snap the orb to a requested stage position (Boss 2026-07-14: 'orb on the top-right card corner').
    const onMoveTo = (e: Event) => {
      const d = (e as CustomEvent).detail as { x: number; y: number } | undefined;
      if (!d) return;
      const next = { x: Math.min(Math.max(d.x, 0), 1920 - 120), y: Math.min(Math.max(d.y, 0), 1080 - 120) };
      setPos(next); try { localStorage.setItem('bcc.orb.pos', JSON.stringify(next)); } catch { /* noop */ }
    };
    window.addEventListener('beast:orb-moveto', onMoveTo);
    try {
      const p = JSON.parse(localStorage.getItem('bcc.orb.pos') || 'null');
      // clamp restored position to the 1920×1080 stage — a bad saved drag must never strand the pane off-screen
      if (p && typeof p.x === 'number' && typeof p.y === 'number') {
        setPos({ x: Math.min(Math.max(p.x, 0), 1920 - 120), y: Math.min(Math.max(p.y, 0), 1080 - 120) });
      }
      const t = JSON.parse(localStorage.getItem('bcc.ia.threads') || 'null'); if (t) setThreads(t);
    } catch { /* fresh */ }
    return () => { window.removeEventListener('uif:space', on); window.removeEventListener('beast:orb-moveto', onMoveTo); };
  }, []);

  /* Esc = rescue: if the chat panel is open, first press closes + re-homes it
     (runs in capture phase so it wins over the Esc→classic-UI escape) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !open) return;
      e.stopImmediatePropagation();
      setOpen(false); setPanelPos(null);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [open]);

  /* drag — grab anywhere on the sphere; release within 120px of a home re-docks.
     SOFT-CLAMP (Boss 2026-07-10: orb + chat stranded outside the frame, nothing
     clickable) — the orb can never leave the reachable stage. */
  /* bounds = the sphere's own edge touches the frame, no padding (Boss):
     container is 160×160, the 96px sphere sits at +32..+128 inside it */
  const clampOrb = (p: { x: number; y: number }) => ({
    x: Math.min(Math.max(p.x, -32), 1920 - 128),
    y: Math.min(Math.max(p.y, -32), 1080 - 128),
  });
  useEffect(() => {
    if (!drag) return;
    // STAGE-LOCAL DRAG (Boss 2026-07-11 'it shifted ... I cannot grab the orb'): the stage is a
    // 1920×1080 surface scaled by `fit`, so a viewport-pixel delta must be divided by `fit` to
    // become a stage-unit delta. The old code added raw viewport deltas to stage coords, so under
    // any non-1.0 scale the orb flew off-target and stranded where it couldn't be grabbed.
    const local = (e: PointerEvent) => clampOrb({ x: drag.bx + (e.clientX - drag.sx) / fit, y: drag.by + (e.clientY - drag.sy) / fit });
    /* TAP vs DRAG threshold (Boss 2026-07-11 'why does it only work on the second click?'): the orb
       used to count ANY pointer move as a drag, so 1-2px of natural hand jitter during a normal
       click set moved=true and cancelled the tap — the first click did nothing, only a steadier
       second click landed. Ignore sub-4px jitter: it's a tap, not a drag. */
    const move = (e: PointerEvent) => {
      if (!moved.current && Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) < 4) return;
      moved.current = true; setPos(local(e));
    };
    const up = (e: PointerEvent) => {
      setDrag(null);
      const home = space === 'hangar' ? { x: 880, y: 130 } : { x: 1726, y: 72 };
      const p = local(e);
      const dock = Math.hypot(p.x - home.x, p.y - home.y) < 120;
      const next = dock ? null : p;
      setPos(next);
      (window as any).__uifDrag = false;
      try { localStorage.setItem('bcc.orb.pos', JSON.stringify(next)); } catch { }
    };
    (window as any).__uifDrag = true; // freeze gaze parallax while dragging (Boss: background must not follow the orb)
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { (window as any).__uifDrag = false; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [drag, space, fit]);

  const inHangar = space === 'hangar';
  const ctxKey = inHangar ? 'hangar' : space;
  const thread = threads[ctxKey] || [];
  const pushMsg = (who: string, t: string) => setThreads(prev => {
    const next = { ...prev, [ctxKey]: [...(prev[ctxKey] || []), { who, t }].slice(-24) };
    try { localStorage.setItem('bcc.ia.threads', JSON.stringify(next)); } catch { }
    return next;
  });
  /* ask — Boss 2026-07-10: NO Gemini, nothing external. The orb IS the real Beast
     (Fable) main gateway session — the exact same POST /api/gw/chat SSE stream the
     chat panel uses. Reply is spoken via LOCAL TTS (/api/voice/speak → Kokoro/Voicebox). */
  const ask = async (msg: string, spoken: boolean) => {
    pushMsg('YOU', msg); setThinking(true);
    /* PERSIST (Boss 2026-07-10 "I want to see the conversation in the Beast"): orb turns
       bypass useChat, so nothing landed in chat-history — the ⚡ BEAST panel looked empty.
       Mirror every turn into slug 'general'; the Chat component's merge-poll picks it up live. */
    fetch('/api/voice/mirror', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user: msg }) }).catch(() => { });
    /* busy-session handling (Boss 2026-07-10 'the orb got stuck'): gateway /chat waits up to 60s
       for the main session to finish its current run before answering. Without feedback the orb
       looks dead. → 6s no-response: say so out loud · 120s: give up cleanly. */
    const ctl = new AbortController();
    let firstByte = false;
    const slow = setTimeout(() => { if (!firstByte && spoken) fetch('/api/voice/speak', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: "I'm finishing another task — give me a moment." }) }).catch(() => { }); }, 6000);
    const hard = setTimeout(() => ctl.abort(), 120_000);
    try {
      /* ENGLISH ONLY on voice (Boss 2026-07-10): session history holds Russian voice
         exchanges → model replied in Russian → Kokoro (English-only) garbled it into
         gibberish. Voice turns carry an explicit English directive. */
      const res = await fetch('/api/gw/chat', {
        signal: ctl.signal,
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: spoken ? `\ud83c\udf99 ${msg}\n\n[voice mode — reply in ENGLISH only, 1-3 short conversational sentences, no markdown/lists/code: it will be spoken aloud. If a topic belongs to a project session, say which and act there.]` : msg }),
      });
      if (!res.ok) throw new Error(`gateway ${res.status}`);
      const reader = res.body?.getReader();
      if (!reader) throw new Error('no stream');
      const dec = new TextDecoder();
      let buf = '', reply = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        firstByte = true; clearTimeout(slow);
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try { const ev = JSON.parse(data); if (ev.type === 'text_delta' && ev.text) reply += ev.text; } catch { }
        }
      }
      if (!reply.trim()) reply = '(no answer)';
      pushMsg('BEAST', reply);
      fetch('/api/voice/mirror', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ beast: reply }) }).catch(() => { });
      // spoken replies BLOCK until Kokoro finishes playing — Beast Mode re-arms the mic only after the voice stops (no self-hearing)
      // TTS backstop: never feed non-English to the English-only voice — that IS the gibberish
      const latin = (reply.match(/[a-zA-Z]/g) || []).length, cyr = (reply.match(/[\u0400-\u04FF]/g) || []).length;
      if (spoken && cyr > latin) { pushMsg('BEAST', '⚠ non-English reply suppressed from voice'); return; }
      if (spoken) {
        setPhase('speaking'); // green — the orb is talking
        await fetch('/api/voice/speak', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: reply }) }).catch(() => { });
      }
    } catch (e: any) {
      const busy = e?.name === 'AbortError';
      pushMsg('BEAST', busy ? '⚠ still busy on another task — ask again in a minute' : `✕ ${e.message}`);
      if (busy && spoken) await fetch('/api/voice/speak', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: "I'm still tied up on another task — ask me again in a minute." }) }).catch(() => { });
    } finally { clearTimeout(slow); clearTimeout(hard); setThinking(false); if (!beastOn) setPhase('idle'); }
  };
  const send = () => { const msg = input.trim(); if (!msg || thinking) return; setInput(''); ask(msg, false); };

  /* BEAST MODE — Boss 2026-07-10 'it's working very faulty… stuck again': the homegrown
     VAD loop is GONE. The orb now mounts the REAL classic-UI BeastMode component
     (src/components/voice/BeastMode.tsx) — hands-free VAD, boss-voice verification,
     busy-session wait with spoken hold-on, sentence-by-sentence speech streaming,
     barge-in interrupt, speech cleaner, chat-history mirroring. One machine, two UIs.
     Orb click toggles it; phases drive the orb colors. */
  const [beastOn, setBeastOn] = useState(false);
  // MUTE state (Boss 2026-07-11): mirror BeastMode's mic-mute so the orb shows a muted glyph.
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    const on = (e: Event) => setMuted(!!(e as CustomEvent).detail);
    window.addEventListener('beast:voice-muted', on);
    return () => window.removeEventListener('beast:voice-muted', on);
  }, []);
  useEffect(() => { if (!beastOn && muted) setMuted(false); }, [beastOn, muted]);

  /* GLOBAL VOICE COMMANDS (Boss 2026-07-13 'control beast with voice — mute, minimize, sessions').
     Registered once; dynamic state (mute, session list) read through refs so closures stay fresh.
     Injected into the voice prompt via voiceCommandsCatalog and run via {{DO:run <id>}}. */
  const mutedRef = useRef(muted); useEffect(() => { mutedRef.current = muted; }, [muted]);
  const allSessionsRef = useRef(allSessions); useEffect(() => { allSessionsRef.current = allSessions; }, [allSessions]);
  useEffect(() => {
    const toggleMute = () => window.dispatchEvent(new CustomEvent('beast:voice-mute-toggle'));
    const offs = [
      registerVoiceCommand({ id: 'panel.expand', scope: 'global', hint: 'open the assistant chat panel',
        phrases: ['expand', 'maximize', 'open the panel', 'show the chat', 'open chat panel'],
        run: () => { setPanelPos(null); setOpen(true); } }),
      registerVoiceCommand({ id: 'panel.minimize', scope: 'global', hint: 'minimize the panel back to the orb',
        phrases: ['minimize', 'minimize to orb', 'collapse', 'hide the panel', 'close the chat panel'],
        run: () => { setOpen(false); } }),
      registerVoiceCommand({ id: 'voice.mute', scope: 'global', hint: 'mute the mic — stop listening',
        phrases: ['mute', 'mute yourself', 'mute the mic', 'stop listening'],
        run: () => { if (!mutedRef.current) toggleMute(); } }),
      registerVoiceCommand({ id: 'voice.unmute', scope: 'global', hint: 'unmute the mic — listen again',
        phrases: ['unmute', 'unmute the mic', 'listen again', 'start listening'],
        run: () => { if (mutedRef.current) toggleMute(); } }),
      registerVoiceCommand({ id: 'sessions.open', scope: 'global', hint: 'open the sessions drawer',
        phrases: ['open sessions', 'show sessions', 'show all sessions', 'show me the sessions', 'sessions'],
        run: () => { setOpen(true); setRailOpen(true); } }),
      registerVoiceCommand({ id: 'sessions.close', scope: 'global', hint: 'close the sessions drawer',
        phrases: ['close sessions', 'hide sessions', 'close the sessions drawer'],
        run: () => { setRailOpen(false); } }),
      registerVoiceCommand({ id: 'sessions.main', scope: 'global', hint: 'switch to the main Beast chat',
        phrases: ['main chat', 'main session', 'beast chat', 'go to main chat', 'back to main'],
        run: () => { setSelSession(null); setRailOpen(false); } }),
      registerVoiceCommand({ id: 'session.switch', scope: 'global', hint: 'switch to a named session (arg = session name)',
        phrases: ['switch to session', 'switch session', 'open session', 'go to session'],
        run: (arg?: string) => {
          const q = (arg || '').toLowerCase().trim(); if (!q) return;
          const list = allSessionsRef.current || [];
          /* FUZZY FALLBACK (2026-07-21, from the live session log): Fable emitted 'tore.ca' for
             the session whose slug is 'tore-ca' — exact/substring match failed and the command
             died SILENTLY while the voice said "Switching now." Normalize to a-z0-9 (tore.ca →
             toreca → matches tore-ca) like workspace.launch already does. */
          const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
          const nq = norm(q);
          const hit = list.find(s => s.name.toLowerCase() === q || s.slug.toLowerCase() === q)
            || list.find(s => s.name.toLowerCase().includes(q) || q.includes(s.name.toLowerCase()))
            || (nq.length >= 3 ? list.find(s => { const fn = norm(s.name), fs = norm(s.slug);
                 return fn.includes(nq) || nq.includes(fn) || fs.includes(nq) || nq.includes(fs); }) : undefined);
          if (hit) { setOpen(true); setSelSession(hit.slug); setRailOpen(false); }
        } }),
      /* FOCUS MODE BY VOICE/CHAT (2026-07-21, Boss: "doesn't open the focus mode" — there was NO
         command for it; only the f-key and workspace.launch could ever set it, so "open focus
         mode" fell into the destinations lookup and failed with "I couldn't find focus mode"). */
      registerVoiceCommand({ id: 'focus.on', scope: 'global', hint: 'enter focus mode — spotlight the cards, dim everything else',
        phrases: ['focus mode', 'enter focus mode', 'open focus mode', 'focus'],
        run: () => window.dispatchEvent(new CustomEvent('beast:focus-set', { detail: { on: true } })) }),
      registerVoiceCommand({ id: 'focus.off', scope: 'global', hint: 'exit focus mode',
        phrases: ['exit focus mode', 'leave focus mode', 'focus off', 'unfocus'],
        run: () => window.dispatchEvent(new CustomEvent('beast:focus-set', { detail: { on: false } })) }),
      /* WORKSPACE LAUNCH (Boss 2026-07-14 'one voice command → four windows open + focus, instant').
         Pops out every named session into its own floating window, minimizes the main Beast chat,
         and snaps the room into focus mode — in a single turn. Names are separated by '|'. */
      registerVoiceCommand({ id: 'workspace.launch', scope: 'global',
        hint: 'open several named sessions as their own windows and enter focus mode — arg = session names separated by | (e.g. workspace.launch Game Tables US | Game Tables Canada | A14)',
        phrases: ['open workspace', 'launch workspace', 'set up my workspace', 'open these sessions', 'pop out these sessions'],
        run: async (arg?: string) => {
          // Accept names separated by | OR , (the model naturally emits commas); strip a trailing
          // 'focus'/'in focus mode' suffix (focus is applied automatically, not a session name).
          const names = (arg || '')
            .replace(/[,]/g, '|')
            .split('|')
            .map(s => s.trim().replace(/\s+in\s+focus(\s+mode)?$/i, '').replace(/^focus$/i, '').trim())
            .filter(Boolean);
          // Fuzzy key: strip everything but a-z0-9 AND drop a trailing plural 's' on each word so
          // 'Game Table US' matches session 'GameTables US' (game-table vs gametables, us vs us).
          const fuzzy = (s: string) => s.toLowerCase()
            .replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean)
            .map(w => w.replace(/s$/, '')).join('');
          if (!names.length) return;
          let list = allSessionsRef.current || [];
          try {
            const d = await fetch('/api/gw/sessions').then(r => r.json());
            const all = Array.isArray(d?.sessions) ? d.sessions : Array.isArray(d) ? d : [];
            if (all.length) list = all;
          } catch { /* fall back to cached list */ }
          const hits: { slug: string | null; name: string }[] = [];
          names.forEach((q0) => {
            const q = q0.toLowerCase();
            const fq = fuzzy(q0);
            const hit = list.find(s => s.name.toLowerCase() === q || s.slug.toLowerCase() === q)
              || list.find(s => s.name.toLowerCase().includes(q) || q.includes(s.name.toLowerCase()))
              || (fq.length >= 3 ? list.find(s => { const fn = fuzzy(s.name), fs = fuzzy(s.slug);
                   return fn.includes(fq) || fq.includes(fn) || fs.includes(fq) || fq.includes(fs); }) : undefined);
            if (hit && !hits.some(w => w.slug === hit.slug)) hits.push({ slug: hit.slug, name: hit.name || hit.slug });
          });
          if (!hits.length) return;
          // LAYOUT (Boss 2026-07-14): a single card opens centered; multiple cards tile in a tidy
          // grid on the 1920×1080 stage (cols = ceil(sqrt n) → 1→1, 2→2, 3-4→2, 5-9→3).
          const n = hits.length;
          const cols = Math.ceil(Math.sqrt(n));
          const rows = Math.ceil(n / cols);
          const GAP = 32, PAD_X = 130, PAD_Y = 110;
          const availW = 1920 - PAD_X * 2 - GAP * (cols - 1);
          const availH = 1080 - PAD_Y * 2 - GAP * (rows - 1);
          const cw = n === 1 ? 660 : Math.max(420, Math.min(780, availW / cols));
          const ch = n === 1 ? 780 : Math.max(420, Math.min(820, availH / rows));
          const gridW = cols * cw + GAP * (cols - 1);
          const gridH = rows * ch + GAP * (rows - 1);
          const ox = (1920 - gridW) / 2, oy = (1080 - gridH) / 2;
          const wins: DetachedWin[] = hits.map((h, i) => {
            const r = Math.floor(i / cols), c = i % cols;
            return { id: `${h.slug ?? '__beast'}-${Date.now()}-${i}`, slug: h.slug, name: h.name,
              pos: { x: ox + c * (cw + GAP), y: oy + r * (ch + GAP) }, size: { w: cw, h: ch }, z: 120 + i };
          });
          // A launch REPLACES the whole workspace: show exactly the cards Boss asked for, tiled
          // fresh, and clear any windows stranded from an earlier launch (Boss 2026-07-14 'look how
          // it opens' — old cards were piling up off-screen). 'Launch these four' means these four only.
          setDetached(wins);
          setActiveId(wins[0]?.id ?? null);
          setOpen(false); setSelSession(null); setRailOpen(false);
          window.dispatchEvent(new CustomEvent('beast:focus-set', { detail: { on: true } }));
          // Park the orb on the top-right corner of the card grid (Boss 2026-07-14).
          window.dispatchEvent(new CustomEvent('beast:orb-moveto', { detail: { x: ox + gridW - 80, y: Math.max(0, oy - 72) } }));
        } }),
    ];
    return () => offs.forEach(off => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const orbTap = () => {
    if (beastOn) { setBeastOn(false); setPhase('idle'); return; }
    /* NO PANEL ON VOICE-START (Boss 2026-07-11 'nothing should shift' / 'the blank black panel
       appears on the left'): starting Beast Mode used to force-open the 880×980 chat panel, which
       is blank here (transcript routes to the MAIN Beast chat) and read as a black sidebar shoving
       the stage left. Now we FORCE it closed on voice-start — covers both the old auto-open and the
       case where the panel was already open. The orb just changes colour; nothing moves. */
    setOpen(false); setRailOpen(false); setSelSession(null);
    /* VOICE ROUTING FIX (Boss 2026-07-11 'the orb doesn't respond — waves, waves, nothing'):
       the orb's turn is only claimed by a chat whose useChat guard matches
       window.__beastVoiceTarget. ChatSurface sets that global to the last session Boss
       looked at and NEVER clears it — so a stale target routed the orb's turn to a session
       whose listener wasn't mounted → no ack → the turn vanished. The orb's transcript
       ALWAYS lands in the MAIN Beast chat (RealChat mounts in the panel below), so pin the
       target to '__beast' — UNLESS a project card is open (Boss 2026-07-15 'it goes to Beast all
       the time, should open the last project'): if a detached card is focused (or one is open),
       the orb targets THAT session so the conversation continues where Boss last was, e.g. Claire. */
    const activeCard = detached.find(d => d.id === activeId) || detached[detached.length - 1];
    // Prefer a real slug; for a game window (slug:null) fall back to the anchor slug it was
    // opened from, so e.g. Claire's voice keeps routing to her while her game is focused.
    (window as any).__beastVoiceTarget = activeCard ? (activeCard.slug ?? activeCard.anchorSlug ?? '__beast') : '__beast';
    setBeastOn(true);
  };

  const floating = !!pos;
  const base = floating
    ? { transform: `translate(${pos!.x - 880}px,${pos!.y - 130}px) scale(.72)` }
    : { transform: inHangar ? 'none' : 'translate(846px,-58px) scale(.42)' };
  const arrowR = inHangar && !floating ? 58 : floating ? 42 : 28;

  return (
    <>
    <div style={{
      position: 'absolute', left: 880, top: 130, width: 160, height: 160, transformOrigin: '50% 50%',
      ...base, transition: drag ? 'none' : 'transform .6s cubic-bezier(.3,.8,.3,1)',
      /* The orb container wraps the Beast panel. Cards live at z~120 in a sibling layer, so the
         panel (trapped in here) could never rise above them (Boss 2026-07-14 'I click on beast
         chat and it's not going over the pop-out cards'). Lift the whole container above the cards
         when the panel is the active surface. */
      zIndex: activeId === '__panel' ? 1000 : (floating || open ? 87 : 46), pointerEvents: 'auto',
    }}>
      {/* MUTE BUTTON (Boss 2026-07-11 'silence my mic so you don't hear my conversations') — tap to
         cut the microphone entirely; tap again to bring it back. Only shown in Beast Mode. */}
      {beastOn && (
        <button data-nodrag onPointerDown={e => e.stopPropagation()}
          onMouseUp={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('beast:voice-mute-toggle')); }}
          title={muted ? 'Muted — click to unmute the mic' : 'Mute the mic'}
          style={{ position: 'absolute', left: '50%', bottom: -30, transform: 'translateX(-50%)', zIndex: 90,
            display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
            border: `1px solid ${muted ? 'rgba(255,120,120,.7)' : 'rgba(125,232,255,.4)'}`,
            background: muted ? 'rgba(60,10,10,.85)' : 'rgba(8,20,32,.8)',
            color: muted ? '#ff8a8a' : '#9ef0ff', fontSize: 14, lineHeight: 1, padding: 0 }}>
          {muted ? '🔇' : '🎙'}
        </button>
      )}
      {/* the REAL Beast Mode engine — headless; orb colors follow its phases */}
      {beastOn && <RealBeastMode onClose={() => { setBeastOn(false); setPhase('idle'); }}
        onPhase={(p: any) => emitPhase(p === 'error' ? 'idle' : p)} />}
      {/* full-size grab underlay (Boss 2026-07-11 'I cannot grab the orb') — the real hit target
         was only the 96px sphere (34px once docked/scaled), while the glow+rings that LOOK grabbable
         are pointer-events:none. This transparent circle makes the whole visible orb draggable. */}
      <div onPointerDown={(e) => { moved.current = false; setDrag({ sx: e.clientX, sy: e.clientY, bx: pos?.x ?? 880, by: pos?.y ?? 130 }); }}
        onMouseUp={() => { if (!moved.current) orbTap(); }}
        style={{ position: 'absolute', inset: 0, borderRadius: '50%', cursor: 'grab', zIndex: 1 }} />
      {/* BEAST face — ferrofluid liquid-metal orb (Boss 2026-07-18, retired the old
         radial-gradient sphere + spinning rings). Skin ripples with phase; pointer
         passes through to the drag underlay above. Phase tints the metal:
         idle cyan · listening amber · thinking violet · speaking green. */}
      <FerrofluidOrb phase={phase as any} size={160} />
      {/* ❯ arrow chip — v6: rides the orb, rotates 45°→225° when open */}
      <div onPointerDown={e => { e.stopPropagation(); moved.current = false; }}
        onMouseUp={e => { e.stopPropagation(); setOpen(o => { if (!o) setPanelPos(null); return !o; }); }}
        style={{ position: 'absolute', left: 80 + arrowR, top: 80 + arrowR, width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', border: '1px solid rgba(125,232,255,.4)', background: 'rgba(3,8,13,.7)', color: '#9ef0ff', fontSize: 22, cursor: 'pointer', transform: `rotate(${open ? 225 : 45}deg)`, transition: 'transform .35s', zIndex: 88 }}>❯</div>
      {/* orb state label removed (Boss 2026-07-19 'we removed all the labels') — keep mic, drop the LISTENING/THINKING/SPEAKING caption under the orb */}
      {/* page chat panel — Boss 2026-07-10: hosts the REAL Beast chat (tool blocks,
         process, streaming — same component as sessions main chat), draggable by header.
         Never a rewrite of the streaming path — hosted per the ChatSurface fallback law. */}
      <div style={{
        position: 'absolute',
        /* smart open placement — the panel must never open outside the frame (Boss):
           prefer beside the orb, flip sides on overflow, clamp vertically into the stage */
        left: open ? (() => {
          const o = pos ?? (inHangar ? { x: 880, y: 130 } : { x: 1726, y: 72 });
          let lx = o.x + 180 + 880 <= 1920 ? 180 : -900;
          return Math.min(Math.max(lx, -o.x + 4), 1920 - 884 - o.x);
        })() : 0,
        top: open ? (() => {
          const o = pos ?? (inHangar ? { x: 880, y: 130 } : { x: 1726, y: 72 });
          return Math.min(Math.max(-20, -o.y + 4), 1080 - 984 - o.y);
        })() : (floating || !inHangar ? -20 : 0),
        /* 880×980 default; user-resizable by the corner grip (Boss 2026-07-11) */
        width: panelSize?.w ?? 880, height: panelSize?.h ?? 980, borderRadius: 16, border: '1px solid rgba(125,232,255,.4)',
        background: 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(4,10,18,.97) 55%)',
        boxShadow: activeId === '__panel'
          ? '0 40px 100px -24px rgba(0,0,0,.9),0 0 0 1px rgba(150,230,255,.4),0 0 42px -4px rgba(120,220,255,.7)'
          : '0 40px 100px -24px rgba(0,0,0,.9),0 0 70px -16px rgba(0,240,255,.35)',
        // Clicking the Beast panel raises it above the cards and marks it active (Boss 2026-07-14
        // 'if I click on the Beast session it should go active as well — it's stuck behind a card').
        padding: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: activeId === '__panel' ? 999 : 85,
        opacity: open ? 1 : 0,
        /* drag = translate delta in stage-local units (stage is a scaled 1920×1080 fit —
           fixed/viewport coords jump offscreen; scar 2026-07-10) */
        transform: `translate(${panelPos?.x || 0}px,${panelPos?.y || 0}px) scale(${open ? 1 : .92})`,
        transformOrigin: pos && pos.x > 960 ? 'right top' : 'left top',
        pointerEvents: open ? 'auto' : 'none', visibility: open ? 'visible' : 'hidden', transition: panelDrag.current ? 'none' : 'opacity .3s,transform .3s,box-shadow .25s',
      }}
        ref={mainPanelRef}
        onPointerDownCapture={() => { setActiveId('__panel'); (window as any).__beastVoiceTarget = '__beast'; }}
        onPointerDown={e => beginPanelDrag(e, e.currentTarget as HTMLElement)}>
        {/* RESIZE GRIP — bottom-right corner. Drags in stage-local units (delta / fit) so it tracks
           the cursor on the scaled stage; clamps to sane min/max. data-nodrag so it never starts a
           panel move. (Boss 2026-07-11 'reduce it to any size'.) */}
        <div data-nodrag onPointerDown={(e) => {
          e.stopPropagation();
          const el = (e.currentTarget.parentElement as HTMLElement);
          const scale = el.getBoundingClientRect().width / el.offsetWidth || 1;
          const sx = e.clientX, sy = e.clientY;
          const bw = panelSize?.w ?? el.offsetWidth, bh = panelSize?.h ?? el.offsetHeight;
          const move = (ev: PointerEvent) => {
            const w = Math.max(280, Math.min(1600, bw + (ev.clientX - sx) / scale));
            const h = Math.max(220, Math.min(1040, bh + (ev.clientY - sy) / scale));
            setPanelSize({ w, h });
          };
          const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
          window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
        }}
          title="drag to resize"
          style={{ position: 'absolute', right: 2, bottom: 2, width: 22, height: 22, cursor: 'nwse-resize', zIndex: 92,
            display: 'grid', placeItems: 'center', color: 'rgba(158,240,255,.6)' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" /></svg>
        </div>
        <style>{`
          .uif-orbchat { zoom: 1.7; } /* orb panel ONLY — global .uif-chat zoom leaked into ChatSurface (scar 2026-07-10) */
          .uif-orbchat > div { height: 100% !important; background: transparent !important; }
          .uif-orbchat > div > div:first-child { display: none; } /* classic header bar */
          /* CARD CHAT (Boss 2026-07-14 'keep it exactly like classic UI'): NO 1.7 zoom (font was huge)
             and KEEP the classic header — that's where the Stop button lives, 'stop on every chat'. */
          .uif-cardchat { zoom: 1; }
          .uif-cardchat > div { height: 100% !important; background: transparent !important; }
        `}</style>
        <div
          style={{ fontFamily: MONO, fontSize: 15, letterSpacing: '.24em', color: '#9ef0ff', cursor: 'grab', userSelect: 'none', padding: '4px 2px', display: 'flex', justifyContent: 'space-between' }}>
          <span>
            <span onMouseEnter={() => setRailOpen(true)} onMouseUp={e => { e.stopPropagation(); setRailOpen(true); }} title="all chat sessions — hover or click to open"
              style={{ cursor: 'pointer', color: railOpen ? '#eaffff' : '#9ef0ff', border: '1px solid rgba(125,232,255,.3)', borderRadius: 5, padding: '2px 7px', marginRight: 10 }}>❮ SESSIONS</span>
            ⠿ BEAST · LIVE SESSION <span style={{ color: '#49677a' }}>· DRAG ANYWHERE</span>
          </span>
          <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {/* MUTE (Boss 2026-07-11 'silence my mic so you don't hear my conversations') — always
               visible in the panel header; cuts the microphone dead until tapped again. */}
            <span onMouseUp={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('beast:voice-mute-toggle')); }}
              title={muted ? 'Muted — click to turn the mic back on' : 'Mute the mic'}
              style={{ cursor: 'pointer', color: muted ? '#ff8a8a' : '#9ef0ff', border: `1px solid ${muted ? 'rgba(255,120,120,.6)' : 'rgba(125,232,255,.3)'}`, borderRadius: 5, padding: '2px 8px', background: muted ? 'rgba(60,10,10,.6)' : 'transparent' }}>
              {muted ? '🔇 MUTED' : '🎙 MUTE'}</span>
            {(panelPos || panelSize) && <span onMouseUp={e => { e.stopPropagation(); setPanelPos(null); setPanelSize(null); }} style={{ cursor: 'pointer', color: '#49677a' }}>↺ RESET</span>}
            <span onMouseUp={e => { e.stopPropagation(); setOpen(false); }} title="minimize to orb" style={{ cursor: 'pointer', color: '#9ef0ff', border: '1px solid rgba(125,232,255,.3)', borderRadius: 5, padding: '2px 9px' }}>—</span>
          </span>
        </div>
        {/* session tab strip — Boss 2026-07-10: open gateway sessions, click to switch */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 2px 2px' }}>
          <span onMouseUp={e => { e.stopPropagation(); setSelSession(null); }} style={{
            fontFamily: MONO, fontSize: 16, letterSpacing: '.18em', cursor: 'pointer', padding: '3px 8px', borderRadius: 5,
            border: `1px solid ${selSession === null ? 'rgba(158,240,255,.65)' : 'rgba(125,232,255,.2)'}`,
            color: selSession === null ? '#eaffff' : '#6e93a8', background: selSession === null ? 'rgba(0,240,255,.1)' : 'transparent',
          }}>⚡ BEAST
            <span data-nodrag onMouseUp={e => { e.stopPropagation(); popOut(null, 'Beast'); }} title="pop Beast out into its own window"
              style={{ cursor: 'pointer', marginLeft: 6, color: '#b49aff' }}>⬚</span>
          </span>
          {liveSessions.filter(s => !isDetached(s.slug)).map(s => (
            <span key={s.slug} onMouseUp={e => { e.stopPropagation(); setSelSession(s.slug); }} style={{
              fontFamily: MONO, fontSize: 16, letterSpacing: '.18em', cursor: 'pointer', padding: '3px 8px', borderRadius: 5,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              border: `1px solid ${selSession === s.slug ? 'rgba(158,240,255,.65)' : 'rgba(125,232,255,.2)'}`,
              color: selSession === s.slug ? '#eaffff' : '#6e93a8', background: selSession === s.slug ? 'rgba(0,240,255,.1)' : 'transparent',
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.state === 'working' ? '#5df2b4' : s.state === 'hibernating' ? '#49677a' : '#9ef0ff' }} />
              {(s.name || s.slug).toUpperCase().slice(0, 18)}
              <span data-nodrag onMouseUp={e => { e.stopPropagation(); popOut(s.slug, s.name || s.slug); }} title="pop out into its own window"
                style={{ cursor: 'pointer', marginLeft: 3, color: '#b49aff' }}>⬚</span>
            </span>
          ))}
        </div>
        {/* sessions drawer — slides over the panel's left edge, lists ALL gateway sessions (Boss 2026-07-10) */}
        {railOpen && (
          <div onPointerDown={e => e.stopPropagation()} onMouseLeave={() => setRailOpen(false)} style={{
            position: 'absolute', left: 0, top: 58, bottom: 12, width: 320, zIndex: 6, borderRadius: '0 12px 12px 0',
            background: 'linear-gradient(170deg,rgba(20,60,85,.97),rgba(4,10,18,.98) 60%)', border: '1px solid rgba(125,232,255,.35)', borderLeft: 'none',
            padding: 12, overflowY: 'auto', boxShadow: '20px 0 60px -20px rgba(0,0,0,.8)',
          }}>
            <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.28em', color: '#49677a', marginBottom: 10 }}>ALL SESSIONS · {allSessions.length}</div>
            <div onMouseUp={() => { setSelSession(null); setRailOpen(false); }}
              onMouseEnter={e => { if (selSession !== null) e.currentTarget.style.background = 'rgba(0,240,255,.07)'; }}
              onMouseLeave={e => { if (selSession !== null) e.currentTarget.style.background = 'transparent'; }}
              style={{
              fontFamily: MONO, fontSize: 15, letterSpacing: '.12em', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, marginBottom: 4, transition: 'background .12s',
              color: selSession === null ? '#eaffff' : '#9fc4d8', background: selSession === null ? 'rgba(0,240,255,.12)' : 'transparent',
            }}>⚡ BEAST · MAIN</div>
            {allSessions.map(s => (
              <div key={s.slug} onMouseUp={() => { setSelSession(s.slug); setRailOpen(false); }}
                onMouseEnter={e => { if (selSession !== s.slug) e.currentTarget.style.background = 'rgba(0,240,255,.07)'; }}
                onMouseLeave={e => { if (selSession !== s.slug) e.currentTarget.style.background = 'transparent'; }}
                style={{
                fontFamily: MONO, fontSize: 15, letterSpacing: '.08em', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, marginBottom: 2,
                display: 'flex', alignItems: 'center', gap: 8, transition: 'background .12s',
                color: selSession === s.slug ? '#eaffff' : s.state === 'hibernating' ? '#56788c' : '#9fc4d8',
                background: selSession === s.slug ? 'rgba(0,240,255,.12)' : 'transparent',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: s.state === 'working' ? '#5df2b4' : s.state === 'hibernating' ? '#3d4654' : '#9ef0ff', boxShadow: s.state === 'working' ? '0 0 8px #5df2b4' : 'none' }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(s.name || s.slug)}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, letterSpacing: '.18em', color: '#49677a', flexShrink: 0 }}>{s.state.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
        {/* Keep the chat MOUNTED whenever Beast Mode is on (not just when the panel is open):
           RealChat's useChat owns the beast:voice-send listener that acks the orb's turn.
           Minimizing the panel used to unmount it → voice turns silently vanished. */}
        <div className="uif-orbchat" style={{ flex: 1, minHeight: 0, overflow: 'hidden', borderRadius: 10 }}>
          {(open || beastOn) && (selSession
            ? (isDetached(selSession)
                ? <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 15, letterSpacing: '.2em', color: '#6e93a8', textAlign: 'center', padding: 20 }}>THIS SESSION IS OPEN IN ITS OWN WINDOW — drag it back here to re-embed</div>
                : <RealSessionChat key={selSession} slug={selSession} name={allSessions.find(s => s.slug === selSession)?.name || selSession} />)
            : (isDetached(null)
                ? <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 15, letterSpacing: '.2em', color: '#6e93a8', textAlign: 'center', padding: 20 }}>BEAST IS OPEN IN ITS OWN WINDOW — drag it back here to re-embed</div>
                : <RealChat />))}
        </div>
      </div>
      {/* TEAR-OFF WINDOWS — sessions pulled out of the panel, each its own draggable chat. */}
    </div>
    {/* DETACHED WINDOWS LIVE OUTSIDE THE ORB TRANSFORM (Boss 2026-07-14 'the orb is attached on the
       left, that's why all the cards go off screen'). Previously these rode inside the orb's
       translate()+scale() container, so moving the orb dragged every card with it and the .72 float
       scale pushed them off the edge. This layer is a plain sibling pinned to the 1920×1080 stage,
       so cards land exactly where the focus-grid places them — independent of the orb. */}
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {detached.map(w => (
        <DetachedChat key={w.id} win={w} active={w.id === activeId} fit={fit} dockZone={dockZone} onDock={dockBack} onMove={moveWin} onRaise={raiseWin} />
      ))}
    </div>
    </>
  );
}

/* light beam under the orb — t4BeamStyle VERBATIM (hangar only) */
function Beam() {
  const [inHangar, setInHangar] = useState(true);
  useEffect(() => {
    const on = (e: Event) => setInHangar((e as CustomEvent).detail === 'hangar');
    window.addEventListener('uif:space', on);
    return () => window.removeEventListener('uif:space', on);
  }, []);
  return (
    <div style={{ position: 'absolute', left: 957, top: 302, width: 6, height: 500, background: 'linear-gradient(180deg,rgba(0,240,255,.3),rgba(0,240,255,.04))', filter: 'blur(2px)', opacity: inHangar ? .4 : 0, animation: inHangar ? 'bccFlick 6s infinite' : 'none', visibility: inHangar ? 'visible' : 'hidden', transition: 'opacity .5s, visibility .5s', pointerEvents: 'none' }} />
  );
}

/* parallax floor — spec lines 36-48 VERBATIM */
function ParallaxFloor({ gx, gy }: { gx: number; gy: number }) {
  return (
    <div style={{ position: 'absolute', inset: 0, transform: `translate(${((960 - gx) * .014).toFixed(2)}px,${((430 - gy) * .02).toFixed(2)}px)`, transition: 'transform .4s ease-out', pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', left: 210, top: 640, width: 1500, height: 340, borderRadius: '50%', border: '1px dashed rgba(125,232,255,.15)' }} />
      <div style={{ position: 'absolute', left: 410, top: 687, width: 1100, height: 246, borderRadius: '50%', border: '1px solid rgba(125,232,255,.09)' }} />
      <div style={{ position: 'absolute', left: 620, top: 735, width: 680, height: 150, borderRadius: '50%', border: '1px solid rgba(125,232,255,.07)', background: 'radial-gradient(closest-side,rgba(0,240,255,.05),transparent 75%)' }} />
      <div style={{ position: 'absolute', left: 210, top: 810, width: 1500, height: 0 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, width: 1500, height: 1500, marginTop: -750, transform: 'scaleY(.2267)', borderRadius: '50%', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'conic-gradient(from 0deg,rgba(0,240,255,.11),transparent 62deg)', animation: 'bccSpin 9s linear infinite' }} />
        </div>
      </div>
      <div style={{ position: 'absolute', left: 560, top: 728, width: 800, height: 170, background: 'radial-gradient(closest-side,rgba(0,240,255,.09),transparent 70%)', filter: 'blur(6px)' }} />
    </div>
  );
}

/* gaze cursor — near-invisible dot that blooms over interactives (Boss 2026-07-10) */
function Reticle({ gx, gy, locked }: { gx: number; gy: number; locked: string | null }) {
  return (
    <div data-reticle style={{ position: 'absolute', left: 0, top: 0, width: 5, height: 5, marginLeft: -2.5, marginTop: -2.5, pointerEvents: 'none', zIndex: 60, transform: `translate(${gx}px,${gy}px)`, transition: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: locked ? 'rgba(191,244,255,.95)' : 'rgba(158,240,255,.3)', boxShadow: locked ? '0 0 10px rgba(0,240,255,.7)' : 'none', transform: locked ? 'scale(1.6)' : 'scale(1)', transition: 'background .18s, box-shadow .18s, transform .18s' }} />
    </div>
  );
}

/* bottom legends — spec lines 577-583 VERBATIM */
function Legends() {
  const st = { display: 'flex', alignItems: 'center', gap: 8 } as const;
  return (
    <>
      {/* dim + small — ambient gesture hints, not information (Boss 2026-07-10) */}
      <div style={{ position: 'absolute', left: 36, bottom: 26, fontFamily: MONO, fontSize: 10, letterSpacing: '.2em', color: '#28343f' }}>
        GAZE TRACKING ACTIVE · MOTION CONTROLLER PAIRED
      </div>
      <div style={{ position: 'absolute', right: 36, bottom: 24, display: 'flex', gap: 16, fontFamily: MONO, fontSize: 10, letterSpacing: '.16em', color: '#324450', opacity: .8 }}>
        <span style={st}><svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke="#56788c" strokeWidth="1.3"><path d="M6 2 L10 7 L6 12 M14 2 L10 7 L14 12" /></svg>PINCH · EXPAND STAT</span>
        <span style={st}><svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke="#56788c" strokeWidth="1.3"><path d="M2 7 H16 M12 3 L16 7 L12 11" /></svg>SWIPE · FLICK</span>
        <span style={st}><svg width="20" height="14" viewBox="0 0 20 14" fill="none" stroke="#56788c" strokeWidth="1.3"><rect x="3" y="3" width="14" height="8" rx="2" /><path d="M7 3 V11 M13 3 V11" /></svg>GRAB · MOVE PANEL</span>
        <span style={st}><svg width="16" height="14" viewBox="0 0 16 14" fill="none" stroke="#56788c" strokeWidth="1.3"><circle cx="8" cy="7" r="4.5" /><circle cx="8" cy="7" r="1" fill="#56788c" /></svg>FIST · BACK</span>
      </div>
    </>
  );
}

/* Voice narration strip — Control Room v4 spec (bottom-center YOU/BEAST lines + 6-bar
   bccWave). Fed by the REAL Beast Mode voice events. Hidden when silent. */
function VoiceStrip() {
  const [uSays, setUSays] = useState('');
  const [bSays, setBSays] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const bBuf = useRef('');
  const hideT = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const arm = () => { if (hideT.current) clearTimeout(hideT.current); hideT.current = setTimeout(() => { setUSays(''); setBSays(''); bBuf.current = ''; }, 12000); };
    const onSend = (e: Event) => { setUSays((e as CustomEvent).detail?.text?.slice(0, 110) || ''); setBSays(''); bBuf.current = ''; setSpeaking(false); arm(); };
    const onDelta = (e: Event) => { bBuf.current = (bBuf.current + ((e as CustomEvent).detail?.text || '')).slice(-160); setBSays(bBuf.current); setSpeaking(true); arm(); };
    const onDone = () => { setSpeaking(false); arm(); };
    window.addEventListener('beast:voice-send', onSend);
    window.addEventListener('beast:voice-delta', onDelta);
    window.addEventListener('beast:voice-done', onDone);
    return () => {
      window.removeEventListener('beast:voice-send', onSend);
      window.removeEventListener('beast:voice-delta', onDelta);
      window.removeEventListener('beast:voice-done', onDone);
      if (hideT.current) clearTimeout(hideT.current);
    };
  }, []);
  if (!uSays && !bSays) return null;
  return (
    <div className="absolute z-[56] flex flex-col items-center" style={{ left: 480, right: 480, bottom: 36, gap: 10, pointerEvents: 'none' }}>
      {uSays && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontFamily: MONO, fontSize: 20, letterSpacing: '.08em', color: '#9fb8c6' }}>
          <span style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(125,232,255,.2)', fontSize: 14, letterSpacing: '.26em', color: '#56788c' }}>YOU</span>
          <span>{uSays}</span>
        </div>
      )}
      {bSays && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontFamily: MONO, fontSize: 21, letterSpacing: '.06em', color: '#bfeaff', textShadow: '0 0 10px rgba(0,240,255,.25)' }}>
          <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(0,240,255,.08)', border: '1px solid rgba(125,232,255,.35)', fontSize: 14, letterSpacing: '.26em', color: '#9ef0ff' }}>BEAST</span>
          <span>{bSays}</span>
          {speaking && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, height: 14 }}>
              {[0, .12, .24, .36, .48, .6].map(d => (
                <span key={d} style={{ width: 2, height: 14, background: '#7de8ff', animation: `bccWave 1.1s ease-in-out ${d}s infinite` }} />
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function Chrome({ children }: { children?: React.ReactNode }) {
  const fit = useFit();
  const stageRef = useRef<HTMLDivElement>(null);
  /* FOCUS MODE (Boss 2026-07-13): a spotlight toggle — the cards + orb rise to the front,
     everything else (header, live row, legends, voice strip, floor) sinks into darkness behind
     a near-black backdrop. Nothing but the four cards and the Beast is left to look at. */
  const [focus, setFocus] = useState(false);
  useEffect(() => {
    try { setFocus(localStorage.getItem('bcc.focusMode') === '1'); } catch { /* noop */ }
  }, []);
  const toggleFocus = useCallback(() => setFocus(f => {
    const n = !f; try { localStorage.setItem('bcc.focusMode', n ? '1' : '0'); } catch { /* noop */ }
    return n;
  }), []);
  // Esc exits focus mode first (before falling through to CLASSIC UI); 'f' toggles it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'f' || e.key === 'F') { toggleFocus(); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFocus]);
  // Voice-driven focus set (Boss 2026-07-14): workspace.launch flips focus on explicitly.
  useEffect(() => {
    const on = (e: Event) => {
      const v = !!(e as CustomEvent).detail?.on;
      setFocus(v); try { localStorage.setItem('bcc.focusMode', v ? '1' : '0'); } catch { /* noop */ }
    };
    window.addEventListener('beast:focus-set', on);
    return () => window.removeEventListener('beast:focus-set', on);
  }, []);
  // useGaze removed (Boss 2026-07-11): its only consumers were the mouse-chasing floor and the
  // custom reticle, both retired. The native cursor is the sole pointer; the floor is static.
  // Boss escape hatch (2026-07-10): Esc always returns to the classic UI, no matter what state the panes are in
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return; // typing ≠ leave
      if (e.defaultPrevented) return; // a deck/overlay consumed Esc (back-navigation) — don't exit
      if (e.key === 'Escape') {
        if (focus) { setFocus(false); try { localStorage.setItem('bcc.focusMode', '0'); } catch { /* noop */ } return; }
        window.location.href = '/';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focus]);
  return (
    <div className="absolute inset-0 overflow-hidden select-none" style={{ background: '#010209' }}>
      {/* 1920×1080 stage, scaled to fit — spec fitStyle VERBATIM */}
      <div ref={stageRef} className="uif-scanlines"
        /* SHIFT FIX (Boss 2026-07-11 'everything shifts left when I click the orb'): traced to the
           stage scrolling itself sideways. When Beast Mode opens, the chat panel + docked orb
           extend past the 1920 stage edge, making this overflow:hidden box internally SCROLLABLE.
           The chat input then autofocuses and the browser scroll-jumps the stage right by ~215px
           to bring it into view — sliding every card, the title, the floor and the orb left as one.
           The document never scrolled, so page-level overflow locks never caught it. Pin it: any
           internal scroll snaps straight back to 0,0. */
        onScroll={e => { const t = e.currentTarget; if (t.scrollLeft || t.scrollTop) t.scrollTo(0, 0); }}
        style={{
        position: 'absolute', left: '50%', top: '50%', width: 1920, height: 1080,
        transform: `translate(-50%,-50%) scale(${fit})`,
        background: 'radial-gradient(1600px 900px at 50% 28%, #0a1220, #04070d 55%, #010209),radial-gradient(2px 2px at 12% 18%, rgba(200,240,255,.18), transparent),radial-gradient(1.5px 1.5px at 78% 12%, rgba(200,240,255,.14), transparent),radial-gradient(1.5px 1.5px at 60% 34%, rgba(200,240,255,.1), transparent),radial-gradient(2px 2px at 30% 8%, rgba(200,240,255,.12), transparent)',
        color: '#d7f2ff', overflow: 'hidden',
      }}>
        {/* Floor no longer chases the mouse (Boss 2026-07-11 'again shifted the screen'):
           gaze-follow made the whole floor slide as the cursor moved, reading as a shifting
           screen. Frozen at centre so nothing moves when you move the mouse. */}
        <ParallaxFloor gx={960} gy={430} />
        {/* logotype row — spec line 50 VERBATIM (+ Boss-mandated classic switch) */}
        <div style={{ position: 'absolute', left: 36, top: 26, zIndex: 400 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
            <span className="uif-metal-text" style={{ fontFamily: 'var(--uif-font-display)', fontSize: 28, letterSpacing: '.34em' }}>BEAST</span>
            <span style={{ fontFamily: MONO, fontSize: 16, letterSpacing: '.42em', color: '#49677a' }}>CONTROL ROOM</span>
          </div>
          {/* small — escape hatch, not a hero button (Boss 2026-07-10 'why so huge?') */}
          <a href="/" data-lock="CLASSIC UI" style={{
            fontFamily: MONO, fontSize: 13, letterSpacing: '.22em', color: '#9fdcff', marginTop: 8,
            display: 'inline-block', textDecoration: 'none', padding: '3px 9px',
            border: '1px solid rgba(120,200,255,.35)', borderRadius: 6, background: 'rgba(10,25,40,.75)',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#eaffff'; e.currentTarget.style.borderColor = 'rgba(160,225,255,.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#9fdcff'; e.currentTarget.style.borderColor = 'rgba(120,200,255,.35)'; }}>
            ⇄ CLASSIC UI
          </a>
        </div>
        <LiveRow />
        {/* FOCUS backdrop — near-black veil above all chrome (top z is 400), below the raised
           cards (420) + orb (430). Fades in when focus mode is on. */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 410, pointerEvents: 'none',
          background: 'radial-gradient(1200px 700px at 50% 42%, rgba(2,6,14,.72), rgba(1,2,7,.94) 70%)',
          backdropFilter: focus ? 'blur(3px)' : 'none', WebkitBackdropFilter: focus ? 'blur(3px)' : 'none',
          opacity: focus ? 1 : 0, transition: 'opacity .55s ease',
        }} />
        {/* stage content — the card deck. In focus mode it fades fully out (Boss 2026-07-13
           'no cards behind it, only the open windows') — not just veiled, gone. */}
        <div className="absolute inset-0 z-10" style={{ opacity: focus ? 0 : 1, pointerEvents: focus ? 'none' : 'auto', transition: 'opacity .5s ease' }}>{children}</div>
        {/* <Beam /> REMOVED per Boss (2026-07-10) — do NOT restore even if a spec port says VERBATIM; Boss removed it twice */}
        {/* orb wrapper raises the Beast above the focus backdrop without touching CoreOrb's own z-logic */}
        <div style={{ position: 'absolute', inset: 0, zIndex: focus ? 430 : 'auto', pointerEvents: 'none' }}>
          <CoreOrb />
        </div>
        <Legends />
        <VoiceStrip />
        {/* FOCUS toggle — always above the backdrop so it can turn itself off */}
        {/* When idle: a normal FOCUS toggle, top-center. When IN focus (Boss 2026-07-14 'not such a
            bright fixed exit sign — dimmed and out of the way'): shrink + dim it and tuck it into the
            bottom-right corner so it never sits over a card. */}
        <button onMouseUp={toggleFocus} data-lock="FOCUS" data-nodrag
          style={focus
            ? { position: 'absolute', right: 22, bottom: 20, zIndex: 440,
                display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999,
                border: '1px solid rgba(125,232,255,.18)', color: 'rgba(158,240,255,.5)',
                fontFamily: MONO, fontSize: 11, letterSpacing: '.22em', background: 'rgba(6,16,26,.4)',
                cursor: 'pointer', opacity: .6, transition: 'opacity .25s,color .25s,border-color .25s' }
            : { position: 'absolute', left: '50%', top: 26, transform: 'translateX(-50%)', zIndex: 440,
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 999,
                border: '1px solid rgba(125,232,255,.3)', color: '#9ef0ff',
                fontFamily: MONO, fontSize: 13, letterSpacing: '.24em', background: 'rgba(6,16,26,.6)',
                cursor: 'pointer', transition: 'color .25s,background .25s,border-color .25s' }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.borderColor = 'rgba(158,240,255,.7)'; e.currentTarget.style.color = '#eaffff'; }}
          onMouseLeave={e => { if (focus) { e.currentTarget.style.opacity = '.6'; e.currentTarget.style.borderColor = 'rgba(125,232,255,.18)'; e.currentTarget.style.color = 'rgba(158,240,255,.5)'; } else { e.currentTarget.style.borderColor = 'rgba(125,232,255,.3)'; e.currentTarget.style.color = '#9ef0ff'; } }}>
          ◎ {focus ? 'EXIT FOCUS' : 'FOCUS'}
        </button>
        {/* Custom reticle removed (Boss 2026-07-11 'the pointer should match the cursor'):
           it drew a second glowing pointer on top of the real macOS cursor, so the two never
           visually coincided. The native cursor is now the single pointer — always exact. */}
        {/* <Reticle gx={gx} gy={gy} locked={locked} /> */}
      </div>
    </div>
  );
}
